<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import Icon from "@/design/primitives/Icon.vue";
import Popover from "@/design/primitives/Popover.vue";
import { tintGradient } from "@/design/tint";
import { useSwitcher, useSwitchWorkspace } from "@/features/organization/api/use-organization";
import { organizationMessages } from "@/features/organization/i18n/organization";
import { useWorkspace } from "@/features/workspace/api/use-workspace";
import { useFileSrc } from "@/shared/files/use-file-src";

/**
 * EM QUAL WORKSPACE EU ESTOU — e, para quem pode, a porta para os outros.
 *
 * ── O SELETOR DA V2, EM CIMA DE OUTRA COISA ──────────────────────────────
 *
 * A forma é a do `UnitSwitcher` da V2: o quadrado com a inicial, o nome, e um
 * chevron que abre a lista com o atual marcado. O que muda é o que está por
 * baixo. Na V2 a unidade era uma coluna dentro do mesmo tenant e trocar era
 * mandar outro cabeçalho; aqui cada workspace é um tenant inteiro, isolado, e
 * trocar é pedir ao servidor a sessão do outro (`POST switcher/:id`, que põe o
 * cookie de renovação dele) e recarregar a página — o arranque restaura JÁ no
 * workspace novo, com caches, streams e identidade recomeçados do zero.
 *
 * ── QUEM VÊ A LISTA ──────────────────────────────────────────────────────
 *
 * O servidor decide (`GET /v3/organization/switcher`): o dono e os admins da
 * matriz recebem todos os workspaces da conta; quem nasceu num workspace recebe
 * só esse. Com uma entrada só não há chevron nem clique — um seletor com um
 * item e uma seta que não abre nada é o empilhar que o dono proíbe.
 *
 * O nome do workspace SAIU do gatilho do `UserMenu`: a barra de cima diz quem
 * eu sou, esta coluna diz onde estou. Dizer duas vezes é empilhar.
 */
const props = defineProps<{ collapsed?: boolean }>();

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: organizationMessages });

const { data: workspace } = useWorkspace();
const { data: switcher } = useSwitcher();
const switching = useSwitchWorkspace();

/**
 * Enquanto a consulta voa, nada — nunca um nome de mentira. Um placeholder com
 * cara de conta é a única coisa pior do que não ter nome nenhum para quem opera
 * duas.
 */
const name = computed(() => workspace.value?.name ?? "");

const logoSrc = useFileSrc(() => workspace.value?.logoFileId ?? null);

/** O quadrado quando não há logo: a primeira letra diz mais do que um vazio. */
const letter = computed(() => initial(name.value));

/**
 * A COR SAI DO NOME, como na V2 — o mesmo hash que tinge todo rosto sem foto
 * (`design/tint`), então cada workspace tem sempre a mesma cor sem coluna nova
 * em lugar nenhum, e a matriz e as unidades se distinguem na lista pela cor.
 */
const tint = computed(() => (name.value ? tintGradient(name.value) : undefined));

const entries = computed(() => switcher.value?.workspaces ?? []);
const canSwitch = computed(() => entries.value.length > 1);
const current = computed(() => switcher.value?.current ?? "");

const open = ref(false);

function initial(text: string): string {
	return text.slice(0, 1).toUpperCase();
}

function choose(tenantId: string) {
	if (tenantId === current.value || switching.isPending.value) {
		open.value = false;
		return;
	}
	switching.mutate(tenantId);
}

const side = computed(() => (props.collapsed ? "right" : "bottom"));
</script>

<template>
	<Popover
		v-if="canSwitch"
		:open="open"
		:label="t('org.switcher.label')"
		:side="side"
		align="start"
		panel-class="w-64 p-1.5"
		@update:open="(value) => (open = value)"
	>
		<template #trigger>
			<button
				type="button"
				class="flex min-w-0 items-center gap-2 rounded-lg py-1.5 text-left transition-colors hover:bg-nav-hover"
				:class="collapsed ? 'justify-center px-0' : 'w-full px-1.5'"
				:title="name"
				:aria-label="t('org.switcher.label')"
				data-testid="workspace-switcher"
			>
				<img
					v-if="logoSrc"
					:src="logoSrc"
					alt=""
					class="size-7 shrink-0 rounded-md object-contain"
				/>
				<span
					v-else-if="tint"
					class="flex size-7 shrink-0 items-center justify-center rounded-md text-2xs font-semibold text-white"
					:style="{ background: tint }"
					aria-hidden="true"
				>
					{{ letter }}
				</span>
				<template v-if="!collapsed">
					<span class="min-w-0 flex-1 truncate text-[13px] font-medium text-fg">{{ name }}</span>
					<Icon
						name="chevronDown"
						class="size-3.5 shrink-0 text-fg-subtle transition-transform"
						:class="open ? 'rotate-180' : ''"
					/>
				</template>
			</button>
		</template>

		<ul class="max-h-80 overflow-y-auto" role="list">
			<li v-for="entry in entries" :key="entry.tenantId">
				<button
					type="button"
					class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-fg transition-colors hover:bg-bg-muted disabled:opacity-60"
					:class="entry.tenantId === current ? 'bg-bg-muted font-medium' : ''"
					:disabled="switching.isPending.value"
					:aria-current="entry.tenantId === current ? 'true' : undefined"
					@click="choose(entry.tenantId)"
				>
					<span
						class="flex size-6 shrink-0 items-center justify-center rounded-md text-3xs font-semibold text-white"
						:style="{ background: tintGradient(entry.name) }"
						aria-hidden="true"
					>
						{{ initial(entry.name) }}
					</span>
					<span class="min-w-0 flex-1 truncate">{{ entry.name }}</span>
					<span v-if="entry.role === 'hq'" class="shrink-0 text-3xs text-fg-subtle">
						{{ t("org.switcher.main") }}
					</span>
					<Icon
						v-if="entry.tenantId === current"
						name="check"
						class="size-4 shrink-0 text-accent"
					/>
				</button>
			</li>
		</ul>
	</Popover>

	<div
		v-else
		class="flex min-w-0 items-center gap-2"
		:class="collapsed ? 'justify-center' : ''"
		:title="name"
	>
		<img
			v-if="logoSrc"
			:src="logoSrc"
			alt=""
			class="size-7 shrink-0 rounded-md object-contain"
		/>
		<span
			v-else-if="tint"
			class="flex size-7 shrink-0 items-center justify-center rounded-md text-2xs font-semibold text-white"
			:style="{ background: tint }"
			aria-hidden="true"
		>
			{{ letter }}
		</span>
		<span v-if="!collapsed" class="truncate text-[13px] font-medium text-fg">{{ name }}</span>
	</div>
</template>
